export const REAL_LOCATIONS_API_PATH = '/wp-json/pfandpirat/v1/locations';
export const REAL_LOCATIONS_PAGE_SIZE = 250;
export const REAL_LOCATIONS_MAX_PAGES = 50;

const LEGACY_MOJIBAKE_REPLACEMENTS = new Map([
  ['\u00c3\u00a4', '\u00e4'],
  ['\u00c3\u00b6', '\u00f6'],
  ['\u00c3\u00bc', '\u00fc'],
  ['\u00c3\u201e', '\u00c4'],
  ['\u00c3\u2013', '\u00d6'],
  ['\u00c3\u0153', '\u00dc'],
  ['\u00c3\u0178', '\u00df'],
  ['\u00c3\u009f', '\u00df'],
]);

export function repairWordPressDisplayText(value) {
  let repaired = String(value ?? '');
  for (const [broken, correct] of LEGACY_MOJIBAKE_REPLACEMENTS) {
    repaired = repaired.replaceAll(broken, correct);
  }
  return repaired;
}
export const DEFAULT_VIEWPORT_PADDING_FACTOR = 0.18;
export const DEFAULT_MIN_VIEWPORT_HALF_SPAN_M = 1100;
export const MAX_LOCATION_REQUEST_HALF_SPAN_M = 1200;
export const MAX_LOCATION_REQUEST_AREA_KM2 = 25;
export const LOCATION_GRID_ZOOM = 15;

function degreesLatitudeForMeters(meters) {
  return Number(meters) / 111320;
}

function degreesLongitudeForMeters(meters, latitude) {
  const safeLatitude = Number.isFinite(Number(latitude)) ? Number(latitude) : 51;
  const cosValue = Math.cos((safeLatitude * Math.PI) / 180);
  const metersPerDegree = 111320 * Math.max(0.2, Math.abs(cosValue));
  return Number(meters) / metersPerDegree;
}

export function bboxAreaSquareKilometers(bbox) {
  const south = Number(bbox?.south);
  const west = Number(bbox?.west);
  const north = Number(bbox?.north);
  const east = Number(bbox?.east);
  if (![south, west, north, east].every(Number.isFinite) || south >= north || west >= east) return Number.POSITIVE_INFINITY;
  const centerLat = (south + north) / 2;
  const heightMeters = (north - south) * 111320;
  const widthMeters = (east - west) * 111320 * Math.max(0.2, Math.abs(Math.cos((centerLat * Math.PI) / 180)));
  return (heightMeters * widthMeters) / 1000000;
}

export function parseCoordinate(value, minimum, maximum) {
  if (value === null || value === undefined || typeof value === 'boolean') return null;
  if (typeof value === 'string' && (value.trim() === '' || value.includes(','))) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= minimum && numeric <= maximum ? numeric : null;
}

export function inspectRealWordPressLocation(raw = {}) {
  const rawId = raw.id ?? raw.ID ?? raw.post_id ?? raw.location_id;
  const id = Number(rawId);
  const latitude = parseCoordinate(
    raw.latitude ?? raw.lat ?? raw.acf?.latitude ?? raw.acf?.lat ?? raw.meta?.latitude ?? raw.meta?.lat,
    -90,
    90
  );
  const longitude = parseCoordinate(
    raw.longitude ?? raw.lng ?? raw.lon ?? raw.acf?.longitude ?? raw.acf?.lng ?? raw.acf?.lon ?? raw.meta?.longitude ?? raw.meta?.lng ?? raw.meta?.lon,
    -180,
    180
  );
  const reasons = [];

  if (!Number.isInteger(id) || id <= 0) reasons.push('invalid-wordpress-id');
  if (latitude === null) reasons.push('invalid-latitude');
  if (longitude === null) reasons.push('invalid-longitude');
  if (latitude === 0 && longitude === 0) reasons.push('zero-zero-coordinate');

  return {
    id: Number.isInteger(id) && id > 0 ? id : null,
    latitude,
    longitude,
    valid: reasons.length === 0,
    reasons,
  };
}

export function normalizeLocationRequestBbox(
  bbox,
  {
    paddingFactor = DEFAULT_VIEWPORT_PADDING_FACTOR,
    minHalfSpanMeters = DEFAULT_MIN_VIEWPORT_HALF_SPAN_M,
    maxHalfSpanMeters = MAX_LOCATION_REQUEST_HALF_SPAN_M,
    maxAreaKm2 = MAX_LOCATION_REQUEST_AREA_KM2,
  } = {}
) {
  if (!bbox) return null;
  const south = Number(bbox.south);
  const west = Number(bbox.west);
  const north = Number(bbox.north);
  const east = Number(bbox.east);
  if (![south, west, north, east].every(Number.isFinite)) return null;
  if (south < -90 || north > 90 || west < -180 || east > 180 || south >= north || west >= east) return null;

  const safePadding = Math.max(0, Math.min(0.5, Number(paddingFactor) || 0));
  const safeMinHalfSpan = Math.max(0, Number(minHalfSpanMeters) || 0);
  const safeMaxHalfSpan = Math.max(safeMinHalfSpan, Number(maxHalfSpanMeters) || MAX_LOCATION_REQUEST_HALF_SPAN_M);
  const centerLat = (south + north) / 2;
  const centerLng = (west + east) / 2;
  const latHalfSpan = Math.min(degreesLatitudeForMeters(safeMaxHalfSpan), Math.max((north - south) * (0.5 + safePadding), degreesLatitudeForMeters(safeMinHalfSpan)));
  const lngHalfSpan = Math.min(degreesLongitudeForMeters(safeMaxHalfSpan, centerLat), Math.max((east - west) * (0.5 + safePadding), degreesLongitudeForMeters(safeMinHalfSpan, centerLat)));
  const padded = {
    south: Math.max(-90, centerLat - latHalfSpan),
    west: Math.max(-180, centerLng - lngHalfSpan),
    north: Math.min(90, centerLat + latHalfSpan),
    east: Math.min(180, centerLng + lngHalfSpan),
  };

  const tileCount = 2 ** LOCATION_GRID_ZOOM;
  const longitudeToTile = (longitude) => Math.floor(((longitude + 180) / 360) * tileCount);
  const latitudeToTile = (latitude) => {
    const radians = (Math.max(-85.05112878, Math.min(85.05112878, latitude)) * Math.PI) / 180;
    return Math.floor(((1 - Math.log(Math.tan(radians) + (1 / Math.cos(radians))) / Math.PI) / 2) * tileCount);
  };
  const tileToLongitude = (tile) => ((tile / tileCount) * 360) - 180;
  const tileToLatitude = (tile) => (Math.atan(Math.sinh(Math.PI * (1 - (2 * tile / tileCount)))) * 180) / Math.PI;
  const westTile = longitudeToTile(padded.west);
  const eastTile = longitudeToTile(Math.max(padded.west, padded.east - Number.EPSILON));
  const northTile = latitudeToTile(padded.north);
  const southTile = latitudeToTile(Math.max(-85.05112878, padded.south));
  let normalized = {
    south: tileToLatitude(southTile + 1),
    west: tileToLongitude(westTile),
    north: tileToLatitude(northTile),
    east: tileToLongitude(eastTile + 1),
  };

  const safeMaxArea = Math.max(1, Number(maxAreaKm2) || MAX_LOCATION_REQUEST_AREA_KM2);
  const normalizedArea = bboxAreaSquareKilometers(normalized);
  if (normalizedArea > safeMaxArea) {
    const scale = Math.sqrt(safeMaxArea / normalizedArea) * 0.98;
    const normalizedCenterLat = (normalized.south + normalized.north) / 2;
    const normalizedCenterLng = (normalized.west + normalized.east) / 2;
    const normalizedLatHalf = ((normalized.north - normalized.south) / 2) * scale;
    const normalizedLngHalf = ((normalized.east - normalized.west) / 2) * scale;
    normalized = {
      south: Number((normalizedCenterLat - normalizedLatHalf).toFixed(6)),
      west: Number((normalizedCenterLng - normalizedLngHalf).toFixed(6)),
      north: Number((normalizedCenterLat + normalizedLatHalf).toFixed(6)),
      east: Number((normalizedCenterLng + normalizedLngHalf).toFixed(6)),
    };
  }

  return {
    ...normalized,
    key: [normalized.south, normalized.west, normalized.north, normalized.east]
      .map((value) => value.toFixed(6))
      .join(','),
    zoom: Number.isFinite(Number(bbox?.zoom)) ? Number(bbox.zoom) : undefined,
  };
}

export function bboxAroundCenter(center, halfSpanMeters = DEFAULT_MIN_VIEWPORT_HALF_SPAN_M) {
  const longitude = Number(Array.isArray(center) ? center[0] : center?.longitude ?? center?.lng);
  const latitude = Number(Array.isArray(center) ? center[1] : center?.latitude ?? center?.lat);
  if (![latitude, longitude].every(Number.isFinite)) return null;
  const latSpan = degreesLatitudeForMeters(halfSpanMeters);
  const lngSpan = degreesLongitudeForMeters(halfSpanMeters, latitude);
  return normalizeLocationRequestBbox({ south: latitude - latSpan, west: longitude - lngSpan, north: latitude + latSpan, east: longitude + lngSpan }, { paddingFactor: 0, minHalfSpanMeters: halfSpanMeters });
}

export function buildLocationsPageUrl(
  bbox,
  { endpoint = REAL_LOCATIONS_API_PATH, page = 1, perPage = REAL_LOCATIONS_PAGE_SIZE } = {}
) {
  if (!bbox?.key) throw new Error('Für die Kartenabfrage ist eine gültige Bounding Box erforderlich.');
  const params = new URLSearchParams({
    bbox: bbox.key,
    view: 'map',
    page: String(Math.max(1, Number(page) || 1)),
    per_page: String(Math.max(1, Math.min(REAL_LOCATIONS_PAGE_SIZE, Number(perPage) || REAL_LOCATIONS_PAGE_SIZE))),
  });
  return `${endpoint}?${params.toString()}`;
}

export function dedupeLocationsByWordPressId(locations = []) {
  const byId = new Map();
  const invalid = [];
  for (const location of Array.isArray(locations) ? locations : []) {
    const inspected = inspectRealWordPressLocation(location);
    if (!inspected.id) {
      invalid.push(location);
      continue;
    }
    if (!byId.has(inspected.id)) byId.set(inspected.id, location);
  }
  return [...byId.values(), ...invalid];
}

function responseHeaderNumber(response, name) {
  const value = Number(response?.headers?.get?.(name));
  return Number.isFinite(value) && value >= 0 ? value : null;
}

export async function fetchWordPressLocationsForBbox({
  bbox,
  endpoint = REAL_LOCATIONS_API_PATH,
  perPage = REAL_LOCATIONS_PAGE_SIZE,
  signal,
  headers,
  ifNoneMatch = '',
  cachedLocations = [],
  onPage,
  fetchImpl = globalThis.fetch,
} = {}) {
  if (!bbox?.key) return { locations: [], pages: 0, total: 0, bbox: null };
  if (typeof fetchImpl !== 'function') throw new Error('Fetch ist für die Locations-Abfrage nicht verfügbar.');

  const pages = [];
  let page = 1;
  let totalPages = 1;
  let total = null;
  let parseMs = 0;
  let etag = '';
  let transferredBytes = 0;

  while (page <= totalPages) {
    if (page > REAL_LOCATIONS_MAX_PAGES) throw new Error('Locations-API überschreitet das sichere Seitenlimit.');
    const requestHeaders = { ...(headers || {}) };
    if (page === 1 && ifNoneMatch) requestHeaders['If-None-Match'] = ifNoneMatch;
    const response = await fetchImpl(buildLocationsPageUrl(bbox, { endpoint, page, perPage }), {
      credentials: 'include',
      signal,
      headers: requestHeaders,
    });
    if (page === 1 && response.status === 304) {
      return {
        locations: sanitizeCachedLocations(cachedLocations),
        pages: 0,
        total: sanitizeCachedLocations(cachedLocations).length,
        bbox,
        etag: ifNoneMatch,
        notModified: true,
        transferredBytes: 0,
        timings: { parseMs: 0 },
      };
    }
    if (!response.ok) throw new Error(`Locations-API antwortete mit HTTP ${response.status}.`);
    if (page === 1) etag = response.headers?.get?.('ETag') || '';
    transferredBytes += responseHeaderNumber(response, 'Content-Length') || 0;
    const parseStartedAt = globalThis?.performance?.now?.() ?? Date.now();
    const payload = await response.json();
    parseMs += (globalThis?.performance?.now?.() ?? Date.now()) - parseStartedAt;
    if (!Array.isArray(payload)) throw new Error('Locations-API lieferte kein Array.');
    pages.push(payload);
    if (typeof onPage === 'function') onPage(dedupeLocationsByWordPressId(pages.flat()), page);

    const headerPages = responseHeaderNumber(response, 'X-WP-TotalPages');
    const headerTotal = responseHeaderNumber(response, 'X-WP-Total');
    if (headerTotal !== null) total = headerTotal;
    totalPages = headerPages !== null ? Math.max(1, headerPages) : (payload.length >= perPage ? page + 1 : page);
    page += 1;
  }

  const locations = dedupeLocationsByWordPressId(pages.flat());
  return { locations, pages: pages.length, total: total ?? locations.length, bbox, etag, notModified: false, transferredBytes, timings: { parseMs } };
}

function sanitizeCachedLocations(locations) {
  return dedupeLocationsByWordPressId(Array.isArray(locations) ? locations : []);
}
